import 'bootstrap/dist/css/bootstrap.min.css';
import './App.scss';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { getUserDetails, loginUser } from 'actions/userActions';
import { authProvider } from 'authProvider';
import { ModalProvider, Anchor } from '@ampol/design-system';
import { userActionTypes } from 'utilities/user-utilities';
import Footer from './components/footer/footer';
import HeaderWrapper from './components/header-wrapper/header-wrapper';
import AmpolCashOrders from './features/ampol-cash/ampol-cash-orders/ampol-cash-orders';
import svg4everybody from 'svg4everybody';
import XeroIntegrationCallback from './components/accounting-integration/xero-integration-callback';
import EverydayRewardsCallback from './components/everyday-rewards/everyday-rewards-callback';
import ErrorPage from './containers/error-page/ErrorPage';
import RouteGuard from './components/route-guard/route-guard';
import SiteLoader from './components/site-loader/site-loader';
import Env from './Env';
import AccountsList from './features/accounts/components/accounts-list/accounts-list';
import CardsListPage from 'features/cards/pages/cards-list-page/cards-list-page';
import UsersListPage from 'features/users/pages/users-list-page/users-list-page';
import AmpolCardAlertBar from './components/_global/ampol-card-alert-bar/ampol-card-alert-bar';
import axios from 'axios';
import dayjs from 'dayjs';
import Links from 'constants/links';
import ToastAlert from 'components/toast-alert/toast-alert';
import { addToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';
import Reports from 'features/reports/pages/reports/reports';
import Maintenance from 'features/maintenance/pages/maintenance/maintenance';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
const queryClient = new QueryClient();

const App = () => {
  const dispatch = useDispatch();

  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const loggedInUserLoading = useSelector((state) => state.userStore.loggedInUserLoading);
  const loggedInUserError = useSelector((state) => state.userStore.loggedInUserError);
  const httpStatusCode = useSelector((state) => state.userStore.httpStatusCode);
  const isAmpolAdmin = useSelector((state) => state.userStore.isAmpolAdmin);
  const unauthorisedUser = useSelector((state) => state.userStore.isUserUnauthorised);
  const loggedInUserId = loggedInUser.userId;

  const toastMessage = useSelector((state) => state.toastAlertStore.message);

  const [scheduledMaintenance, setScheduledMaintenance] = useState({
    id: '',
    title: '',
    message: '',
    startDate: '',
    endDate: '',
  });
  const [maintenanceMessageActive, setMaintenanceMessageActive] = useState(false);

  const setupScheduledMaintenanceInterceptor = () => {
    let timeoutId;

    axios.interceptors.response.use(
      function (response) {
        return response;
      },
      (error) => {
        if (
          error?.response?.status === 503 &&
          error?.response?.data?.errorMessage.includes('startDate') &&
          error?.response?.data?.errorMessage.includes('endDate')
        ) {
          try {
            const { id, title, message, startDate, endDate } = JSON.parse(error.response.data.errorMessage);

            const end = dayjs(endDate);
            const now = dayjs();
            const duration = end.diff(now);

            if (duration > 0) {
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              const notificationHidden = document.cookie.includes(`ampol-card-alert-dismissed=${id}`);
              setScheduledMaintenance({
                id,
                title,
                message,
                startDate,
                endDate,
              });
              setMaintenanceMessageActive(notificationHidden ? false : !!id);

              setTimeout(() => {
                isAmpolAdmin ? resetScheduledMaintenance() : window.location.reload();
              }, duration);
            }
          } catch (e) {
            console.log('Error parsing error message:', e);
          }
        }
        return Promise.reject(error);
      }
    );
  };

  const setupSessionExpiredInterceptor = () => {
    axios.interceptors.response.use(
      function (response) {
        return response;
      },
      (error) => {
        if (error?.response?.status === 409 && error?.response?.data?.errorMessage === 'Hierarchy change') {
          dispatch(addToastAlert(Alert.SESSION_EXPIRED, 'error', true, 'center', 'top'));

          // Set a timeout to refresh the page after 10 seconds
          setTimeout(() => {
            window.location.reload();
          }, 10000);
        } else if (
          error?.response?.status === 403 &&
          error?.response?.data?.errorMessage?.includes('does not have access to account') &&
          toastMessage !== Alert.SESSION_EXPIRED
        ) {
          dispatch(addToastAlert(Alert.NO_ACCESS, 'error', true, 'center', 'top'));
        }
        return Promise.reject(error);
      }
    );
  };

  const resetScheduledMaintenance = () => {
    setScheduledMaintenance({
      id: '',
      title: '',
      message: '',
      startDate: '',
      endDate: '',
    });
  };

  useEffect(() => {
    if (Env.getIsZonkaEnabled() && typeof _zf === 'function' && loggedInUser?.emailId) {
      window._zf('variables', { contact_email: loggedInUser.emailId });
    }
  }, [loggedInUser?.emailId]);

  useEffect(() => {
    dispatch(loginUser());
    dispatch(getUserDetails());
    svg4everybody();

    setupScheduledMaintenanceInterceptor();
    setupSessionExpiredInterceptor();
  }, []);

  useEffect(() => {
    if (loggedInUserId) {
      window.dataLayer.push({
        event: 'Card_Portal_Login_Successful',
        B2CGUID: loggedInUserId,
      });
    }
  }, [loggedInUserId]);

  const logoutUser = () => {
    authProvider.logoutRedirect();
  };

  const maintenanceNotificationClose = () => {
    setMaintenanceMessageActive(false);
  };

  const buildMaintenancePage = (logoutUser, details) => {
    return (
      <>
        <ErrorPage fullScreen={true} heading={'Scheduled Maintenance'} message={details.message} />
      </>
    );
  };

  const buildErrorPage = (logoutUser) => {
    return (
      <>
        <ErrorPage
          fullScreen={true}
          heading={"You don't have access to Card Portal"}
          message={
            <span>
              We&apos;re sorry about that. Please <Anchor href={Links.CONTACT_US}>contact us</Anchor> to request access.
            </span>
          }
          buttonLabel={'logout'}
          buttonAction={logoutUser}
        />
      </>
    );
  };

  const buildApp = () => {
    return (
      <>
        {Env.getMockSwitch() === 'off' && (
          <>
            <noscript>
              <iframe
                src='https://www.googletagmanager.com/ns.html?id=GTM-M3F83VP'
                height='0'
                width='0'
                style='display:none;visibility:hidden'
              ></iframe>
            </noscript>

            <script
              id='google-analytics'
              // eslint-disable-next-line react/no-unknown-property
              strategy='afterInteractive'
              dangerouslySetInnerHTML={{
                __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-M3F83VP')`,
              }}
            />
          </>
        )}
        <QueryClientProvider client={queryClient}>
          <ModalProvider>
            <div id='bodyScroll' className='wrapper scroll'>
              <BrowserRouter>
                {scheduledMaintenance?.id !== '' && isAmpolAdmin && (
                  <AmpolCardAlertBar
                    colour='red'
                    id={scheduledMaintenance.id}
                    message={'Scheduled maintenance active. Some features may be inactive'}
                    handleCloseFn={maintenanceNotificationClose}
                    isActive={maintenanceMessageActive}
                  />
                )}
                <ToastAlert />
                <HeaderWrapper routes={routes} maintenanceActive={maintenanceMessageActive} />
                <BaseLayout />
                <Footer />
              </BrowserRouter>
            </div>
          </ModalProvider>
        </QueryClientProvider>
      </>
    );
  };

  if (loggedInUserLoading && loggedInUser.userId === '') {
    return <SiteLoader />;
  }

  if ((loggedInUserError && httpStatusCode != null && httpStatusCode === 403) || unauthorisedUser) {
    return buildErrorPage(logoutUser);
  }

  if (scheduledMaintenance && scheduledMaintenance.id !== '' && !isAmpolAdmin) {
    return buildMaintenancePage(logoutUser, scheduledMaintenance);
  }

  return buildApp();
};

const routes = [
  ...(!Env.isAdminPortal()
    ? [
        {
          name: 'myAccounts',
          path: '/',
          exact: true,
          text: 'Accounts',
          component: AccountsList,
          accessRoles: [userActionTypes.AccountsRead],
          portalType: ['card'],
          crumb: () => <div>Accounts</div>,
          nav: 'main',
          portalAccess: ['card', 'cash'],
        },
      ]
    : []),
  ...(Env.isAdminPortal()
    ? [
        {
          name: 'userAdmin',
          path: '/',
          exact: true,
          text: 'Users',
          component: UsersListPage,
          accessRoles: [userActionTypes.UsersRead],
          crumb: () => <div>Users</div>,
          nav: 'main',
          portalAccess: ['admin'],
        },
      ]
    : []),
  ...(Env.isAmpolCashEnabled()
    ? [
        {
          name: 'ampolCash',
          path: '/cash',
          exact: true,
          text: 'Cash',
          component: AmpolCashOrders,
          accessRoles: [userActionTypes.CashRead],
          crumb: () => <div>Cash</div>,
          nav: 'main',
          portalAccess: ['cash', 'admin'],
        },
      ]
    : []),
  {
    name: 'myAmpolCards',
    path: '/cards',
    text: 'Cards',
    component: CardsListPage,
    crumb: () => <div>Cards</div>,
    nav: 'main',
    accessRoles: [userActionTypes.CardsRead],
    portalAccess: ['card'],
  },
  // subMenu options for Cards will be hidden navigation paths to redirect to Main Cards page
  {
    name: 'transactions',
    path: '/transactions',
    text: 'Transactions',
    component: CardsListPage,
    accessRoles: [userActionTypes.CardsRead],
    nav: '',
    portalAccess: ['card'],
  },
  {
    name: 'myAmpolCardsTransferCard',
    path: '/transfercard',
    text: 'Pending Transfers',
    component: CardsListPage,
    accessRoles: [userActionTypes.CardsRead],
    nav: '',
    portalAccess: ['card'],
  },
  {
    name: 'Reports',
    path: '/reports',
    text: 'Reports',
    component: Reports,
    crumb: () => <div>Reports</div>,
    accessRoles: [userActionTypes.ReportsRead],
    nav: 'main',
    portalAccess: ['card', 'cash'],
  },
  // subMenue options for Reports will be hidden navigation paths to redirect to Main Reports page
  {
    name: 'reportsSchedule',
    path: '/reportsSchedule',
    text: 'Reports Schedule',
    component: Reports,
    accessRoles: [userActionTypes.ReportsRead],
    nav: '',
    portalAccess: ['card'],
  },
  {
    name: 'invoicesStatements',
    path: '/invoicesStatements',
    text: 'Invoices/Statements',
    component: Reports,
    accessRoles: [userActionTypes.ReportsRead],
    nav: '',
    portalAccess: ['card', 'cash'],
  },
  ...(!Env.isAdminPortal()
    ? [
        {
          name: 'userAdmin',
          path: '/userAdmin',
          text: 'Users',
          component: UsersListPage,
          accessRoles: [userActionTypes.UsersRead],
          crumb: () => <div>Users</div>,
          nav: 'main',
          portalAccess: ['card', 'cash'],
        },
      ]
    : []),
  // subMenu options for Admin: Maintenance will be hidden navigation paths to redirect to Main Maintenance page
  {
    name: 'Maintenance',
    path: '/maintenance',
    text: 'Maintenance',
    component: Maintenance,
    crumb: () => <div>Maintenance</div>,
    nav: 'main',
    portalAccess: ['card', 'cash', 'admin'],
    accessRoles: [userActionTypes.MaintenanceRead],
  },
  {
    name: 'cardMaintenance',
    path: '/cardMaintenance',
    text: 'Card Maintenance',
    component: Maintenance,
    nav: '',
    accessRoles: [userActionTypes.MaintenanceRead],
    portalAccess: ['card', 'cash', 'admin'],
  },
  {
    name: 'scheduledMaintenance',
    path: '/scheduledMaintenance',
    text: 'Scheduled Maintenance',
    component: Maintenance,
    nav: '',
    accessRoles: [userActionTypes.MaintenanceRead],
    portalAccess: ['card', 'cash', 'admin'],
  },
  {
    name: 'xeroIntegrationCallback',
    path: '/xeroIntegrationCallback',
    component: XeroIntegrationCallback,
    nav: '',
    portalAccess: ['card', 'cash'],
  },
  {
    name: 'everydayRewardsCallback',
    path: '/everydayRewardsCallback',
    component: EverydayRewardsCallback,
    nav: '',
    portalAccess: ['card', 'cash'],
  },
  {
    name: 'error404',
    path: '*',
    component: () => (
      <ErrorPage
        heading={'Sorry that page could not be found.'}
        errorCode={404}
        message={'The page you requested does not exist, or is currently unavailable.'}
        buttonLabel={'take me back'}
        buttonAction={() => (window.location.href = '/')}
      />
    ),
    nav: '',
  },
];

const renderRoutes = (routes) => {
  return routes?.map((route) => {
    const primaryRoute = (
      <Route
        key={route.path}
        path={route.path}
        element={
          <RouteGuard
            component={route.component}
            accessRoles={route.accessRoles}
            portalAccess={route.portalAccess}
            {...route}
          />
        }
      />
    );

    return route.subMenuItems ? [primaryRoute, ...renderRoutes(route.subMenuItems)] : primaryRoute;
  });
};

const BaseLayout = () => (
  <main className='content'>
    <Routes>{renderRoutes(routes)}</Routes>
  </main>
);

export default App;
